import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Vote, Plus, Trash2, X, CheckCircle2, Users2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatTime } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PollBar = ({ label, votes, total, highlight }) => {
  const pct = total > 0 ? Math.round((votes / total) * 100) : 0;
  return (
    <div className="space-y-1" data-testid="poll-bar">
      <div className="flex justify-between text-sm">
        <span className={`font-medium ${highlight ? 'text-[#2563eb]' : 'text-[#334155]'}`}>{label}</span>
        <span className="text-[#64748b] text-xs">{votes} vote{votes === 1 ? '' : 's'} · {pct}%</span>
      </div>
      <div className="h-2 bg-[#f1f5f9] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${highlight ? 'bg-[#2563eb]' : 'bg-[#cbd5e1]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const CreatePollForm = ({ properties, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Options tracked as {id, value} so removing a row keeps stable React keys
  // (index-based keys would swap input focus between rows on removal).
  const nextOptId = useRef(3);
  const [options, setOptions] = useState([
    { id: 1, value: '' },
    { id: 2, value: '' },
  ]);
  const [endDate, setEndDate] = useState('');
  // Empty = all properties (global). Non-empty = only residents in these properties see the poll.
  const [targetProperties, setTargetProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  const addOpt = () => setOptions([...options, { id: nextOptId.current++, value: '' }]);
  const removeOpt = (id) =>
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((o) => o.id !== id)));
  const updateOpt = (id, v) =>
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, value: v } : o)));

  const toggleProperty = (id) =>
    setTargetProperties((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Title is required');
    const cleaned = options.map((o) => o.value.trim()).filter(Boolean);
    if (cleaned.length < 2) return toast.error('Add at least 2 non-empty options');
    setSaving(true);
    try {
      await axios.post(
        `${API}/polls`,
        {
          title: title.trim(),
          description: description.trim(),
          options: cleaned,
          end_date: endDate || null,
          target_properties: targetProperties,
        },
        { withCredentials: true }
      );
      toast.success('Poll created and residents notified');
      setTitle(''); setDescription('');
      nextOptId.current = 3;
      setOptions([{ id: 1, value: '' }, { id: 2, value: '' }]);
      setEndDate('');
      setTargetProperties([]);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create poll');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="create-poll-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-md bg-[#2563eb]/10 flex items-center justify-center">
          <Plus className="h-5 w-5 text-[#2563eb]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Create a new poll</h3>
          <p className="text-xs text-[#64748b]">Residents get a push notification the moment you publish.</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="create-poll-form">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Title *</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Choose the new gym equipment" data-testid="poll-title-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional context for residents" data-testid="poll-description-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Options *</Label>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={o.id} className="flex gap-2" data-testid={`poll-option-row-${i}`}>
                <Input value={o.value} onChange={(e) => updateOpt(o.id, e.target.value)} placeholder={`Option ${i + 1}`} data-testid={`poll-option-${i}`} />
                {options.length > 2 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeOpt(o.id)} className="text-red-500 hover:bg-red-50" data-testid={`remove-poll-option-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addOpt} className="mt-2 border-dashed" data-testid="add-poll-option">
            <Plus className="mr-2 h-3.5 w-3.5" /> Add option
          </Button>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Target properties</Label>
          <p className="text-xs text-[#94a3b8] -mt-1">
            {targetProperties.length === 0
              ? 'Leave empty to send to residents across all properties.'
              : `Only residents in ${targetProperties.length} selected propert${targetProperties.length === 1 ? 'y' : 'ies'} will see this poll.`}
          </p>
          {properties.length === 0 ? (
            <p className="text-xs text-[#94a3b8] italic">Loading properties…</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border border-[#e2e8f0] rounded-md bg-[#f8fafc]" data-testid="poll-property-list">
              {properties.map((p) => {
                const checked = targetProperties.includes(p._id);
                return (
                  <label
                    key={p._id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                      checked ? 'bg-[#2563eb]/10 text-[#0f172a]' : 'hover:bg-white text-[#334155]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProperty(p._id)}
                      className="h-3.5 w-3.5 accent-[#2563eb]"
                      data-testid={`poll-property-checkbox-${p._id}`}
                    />
                    <Building2 className="h-3 w-3 text-[#94a3b8] flex-shrink-0" />
                    <span className="truncate">{p.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Ends on (optional)</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="poll-end-date-input" />
        </div>
        <Button type="submit" disabled={saving} className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white disabled:opacity-60" data-testid="submit-poll-button">
          {saving ? 'Publishing…' : 'Publish poll'}
        </Button>
      </form>
    </Card>
  );
};

const PollCard = ({ poll, onVote, canManage, onDelete, propertiesById = {} }) => {
  const [voting, setVoting] = useState(false);
  const [pending, setPending] = useState(null); // index the user picked but hasn't confirmed yet
  const total = poll.total_votes ?? Object.values(poll.votes || {}).reduce((s, v) => s + Number(v || 0), 0);
  const winningIdx = useMemo(() => {
    if (!total) return -1;
    let best = -1;
    let max = -1;
    Object.entries(poll.votes || {}).forEach(([k, v]) => {
      if (Number(v) > max) { max = Number(v); best = Number(k); }
    });
    return best;
  }, [poll.votes, total]);
  const closed = poll.end_date && new Date(poll.end_date) < new Date();
  const disabled = poll.has_voted || closed;

  const submit = async () => {
    if (pending == null) return;
    setVoting(true);
    try {
      await onVote(poll._id, pending);
      setPending(null);
    } finally {
      setVoting(false);
    }
  };

  return (
    <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid={`poll-card-${poll._id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit' }} data-testid="poll-title">{poll.title}</h3>
          {poll.description && <p className="text-sm text-[#64748b] mt-1">{poll.description}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-[#94a3b8] flex-wrap">
            <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" /> {total} vote{total === 1 ? '' : 's'}</span>
            {poll.end_date && (
              <span>{closed ? 'Closed' : 'Ends'} {formatDate(poll.end_date)}</span>
            )}
            {poll.created_by && <span>by {poll.created_by}</span>}
            {canManage && Array.isArray(poll.target_properties) && poll.target_properties.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#2563eb]/10 text-[#2563eb] font-medium" data-testid="poll-target-badge">
                <Building2 className="h-3 w-3" />
                {poll.target_properties
                  .map((pid) => propertiesById[pid]?.name || 'Unknown')
                  .slice(0, 3)
                  .join(', ')}
                {poll.target_properties.length > 3 && ` +${poll.target_properties.length - 3}`}
              </span>
            )}
            {canManage && (!poll.target_properties || poll.target_properties.length === 0) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#64748b] font-medium" data-testid="poll-target-badge">
                <Building2 className="h-3 w-3" /> All properties
              </span>
            )}
          </div>
        </div>
        {canManage && (
          <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => onDelete(poll._id)} data-testid={`delete-poll-${poll._id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {disabled ? (
        <div className="space-y-3" data-testid="poll-results">
          {poll.options.map((opt, i) => (
            <PollBar key={`${poll._id}-opt-${i}`} label={opt} votes={Number(poll.votes?.[i] || 0)} total={total} highlight={i === winningIdx} />
          ))}
          {poll.has_voted && !closed && (
            <p className="text-xs text-[#10b981] flex items-center gap-1 mt-1"><CheckCircle2 className="h-3 w-3" /> You already voted.</p>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="poll-choices">
            {poll.options.map((opt, i) => (
              <button
                key={`${poll._id}-vote-${i}`}
                type="button"
                onClick={() => setPending(i)}
                className={`w-full text-left px-4 py-3 rounded-md border text-sm transition-all ${
                  pending === i
                    ? 'border-[#2563eb] bg-[#2563eb]/5 text-[#0f172a] font-medium'
                    : 'border-[#e2e8f0] hover:border-[#cbd5e1] text-[#334155]'
                }`}
                data-testid={`vote-choice-${i}`}
              >
                {opt}
              </button>
            ))}
          </div>
          <Button
            onClick={submit}
            disabled={pending == null || voting}
            className="w-full mt-4 bg-[#2563eb] hover:bg-[#1d4ed8] text-white disabled:opacity-60"
            data-testid={`submit-vote-${poll._id}`}
          >
            {voting ? 'Submitting…' : pending == null ? 'Select an option to vote' : 'Confirm vote'}
          </Button>
        </>
      )}
    </Card>
  );
};

export const Polls = () => {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'management';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/polls`, { withCredentials: true });
      setPolls(data);
    } catch (_) {
      toast.error('Failed to load polls');
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const { data } = await axios.get(`${API}/properties`, { withCredentials: true });
      const props = (data || []).slice().sort((a, b) => {
        const aNum = parseInt((a.name.match(/\d+/) || [0])[0], 10);
        const bNum = parseInt((b.name.match(/\d+/) || [0])[0], 10);
        return aNum - bNum;
      });
      setProperties(props);
    } catch (_) {
      // Non-fatal — form will show a friendly "Loading properties…" hint
    }
  };

  useEffect(() => {
    load();
    if (isAdmin) loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const propertiesById = useMemo(() => {
    const m = {};
    properties.forEach((p) => { m[p._id] = p; });
    return m;
  }, [properties]);

  const vote = async (pollId, optionIndex) => {
    try {
      await axios.post(`${API}/polls/vote`, { poll_id: pollId, option_index: optionIndex }, { withCredentials: true });
      toast.success('Vote recorded');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to vote');
    }
  };

  const del = async (pollId) => {
    if (!window.confirm('Delete this poll?')) return;
    try {
      await axios.delete(`${API}/polls/${pollId}`, { withCredentials: true });
      toast.success('Poll deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="polls-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Polls & AGM Voting</h1>
        <p className="text-sm text-[#64748b]">
          {isAdmin ? 'Publish polls and see live results as residents vote.' : 'Cast your vote on community decisions and see the results in real time.'}
        </p>
      </div>

      {isAdmin && <CreatePollForm properties={properties} onCreated={load} />}

      <div>
        <h2 className="text-2xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
          {isAdmin ? 'All polls' : 'Open polls'} ({polls.length})
        </h2>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
          </div>
        ) : polls.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
            <Vote className="h-10 w-10 text-[#94a3b8] mx-auto mb-3" />
            <p className="text-[#64748b] text-sm">No polls yet.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {polls.map((p) => (
              <PollCard key={p._id} poll={p} onVote={vote} canManage={isAdmin} onDelete={del} propertiesById={propertiesById} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Polls;
